'use client'
import { useContext } from 'react'
import { useMemoizedContentGetter } from 'utils/contentful'
import Section from 'components/Section'
import { MediaImageStandard } from 'components/Contentful/MediaImageStandard/MediaImageStandard'
import { LinkButtonNoMui as LinkButton } from 'components/LinkButtonNoMui/LinkButtonNoMui'
import { TypographyV2 as Typography } from 'components/Typography/TypographyV2'
import classNames from 'classnames'
import { useGraduate } from 'providers/GraduateProvider'
import { TabButton } from 'components/TabButton'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Ok from 'components/Svgs/Ok'
import ThumbUp from 'components/Svgs/ThumbUp'
import useInactivityStatus from 'hooks/useInactivityStatus'
import useIsInViewport from 'hooks/useIsInViewport'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { TABS_VARIANT, LENDING_VARIANT, TAB_QUOTE_STYLE } from './constants'
import { TrustMarkersCarousel } from './TrustMarkersCarousel'
import { TabsCarousel } from './TabsCarousel'
import { DefaultCarousel } from './DefaultCarousel'
import { LayoutContext } from 'providers/LayoutProvider'
import { AchieveLink } from 'components/AchieveLink/AchieveLink'
import { Button } from '@achieve/ascend'
import TrustpilotRatingBadge from 'components/TrustpilotRatingBadge'
// ─── Constants ──────────────────────────────────────────────────────────────────

const XL_VARIANT = 'xl'

// ─── TrustPilotBadge (shared, memoized) ─────────────────────────────────────────

const TrustPilotBadge = memo(function TrustPilotBadge({ trustPilot, className }) {
  if (!trustPilot) return null
  return (
    <div className={classNames('flex items-center justify-center', className)}>
      <MediaImageStandard
        content={trustPilot?.mediaContent}
        alt={trustPilot?.mediaAltText}
        fetchPriority="high"
        preload={true}
      />
    </div>
  )
})

// ─── TabBar ─────────────────────────────────────────────────────────────────────

const TabBar = memo(function TabBar({ items, selected, onTabClick }) {
  return (
    <div className="mt-2 flex items-start justify-start">
      <div className="w-full flex">
        <div className="border-solid border-0 border-b-[3px] border-gray-300 gap-3 sm:gap-[31px] flex w-fit">
          {items.map((item, index) => {
            if (!item?.eyebrow) return null
            return (
              <TabButton
                key={item.eyebrow}
                onClick={() => onTabClick(index)}
                color="primary"
                variant="text"
                size="small"
                className="rounded-none justify-center hover:bg-transparent sm:[word-spacing:1px] whitespace-nowrap p-0 pb-4 font-bold"
              >
                <div className="inline-block relative">
                  <Typography
                    content={item.eyebrow}
                    variant="labelSm"
                    className={classNames(
                      'text-center',
                      selected === index ? 'font-bold text-[#3300FF]' : 'font-medium text-[#5C708A]'
                    )}
                  />
                  {selected === index && (
                    <div
                      className="absolute left-0 right-0 h-[3px] rounded-[2px] font-extrabold"
                      style={{
                        background: 'linear-gradient(98deg, #30F -83.7%, #68D6F2 106.83%)',
                        bottom: '-19px',
                        zIndex: 1,
                      }}
                    />
                  )}
                </div>
              </TabButton>
            )
          })}
        </div>
      </div>
    </div>
  )
})

// ─── TabsContent ────────────────────────────────────────────────────────────────

function TabsContent({ items, selected, selectedItem, tabStyle, trustPilot, viewRef, onTabClick }) {
  const isLending = tabStyle === LENDING_VARIANT
  const isQuote = tabStyle === TAB_QUOTE_STYLE

  return (
    <div className="xl:max-w-[490px]" ref={viewRef}>
      <div className={isLending ? 'lg:w-[490px]' : 'lg:w-[460px]'}>
        <TabBar items={items} selected={selected} onTabClick={onTabClick} />

        <Typography
          content={selectedItem?.title?.json}
          variant={isLending ? 'headingXl' : 'displayXl'}
          mobileVariant="headingLg"
          responsive={true}
          className={classNames(
            'transition-all duration-100 ease-in-out text-left bg-clip-text text-transparent mt-6',
            isQuote || isLending
              ? 'bg-[linear-gradient(178deg,#3C14FF_-8.4%,#1DB6FF_117.71%)]'
              : 'bg-[linear-gradient(132deg,#000C41_34.76%,#286BFC_135.1%)]'
          )}
        />

        {selectedItem?.config?.factsList?.length > 0 && (
          <div className="flex items-center justify-start py-6">
            <ul className="m-0 p-0 transition-all duration-100 ease-in-out">
              {selectedItem.config.factsList.map((fact, i) => (
                <li
                  key={`icon-list-item-${i}`}
                  className="flex flex-row flex-nowrap text-left mb-2 md:mb-[18px] justify-start items-center"
                >
                  <Ok className="h-[25px] w-[25px] text-[#3300ff]" />
                  <Typography
                    responsive={true}
                    variant="bodyMd"
                    mobileVariant="bodySm"
                    className="pl-2"
                    content={fact}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}

        {selectedItem?.config?.ctaURL && (
          <div
            className={classNames(
              'transition-all duration-100 ease-in-out',
              isLending ? 'xl:max-w-[219px]' : 'xl:max-w-[340px]'
            )}
          >
            <LinkButton
              track={{
                list_name: 'Tab CTA click',
                click_id: selectedItem?.config?.ctaText,
                click_text: selectedItem?.config?.ctaText,
                click_url: selectedItem?.config?.ctaURL,
                track_event: 'internal_campaign_click',
                event_action: 'button_click',
                event_type: 'button_click',
                slide_viewed: selectedItem?.eyebrow,
                ...selectedItem?.config?.ctaTrack,
              }}
              data-testid="slider-cta-button"
              typographicOptions={{
                fontWeight: 'bold',
              }}
              className="transition-all duration-100 ease-in-out"
              content={{
                linkHref: selectedItem?.config?.ctaURL,
                linkText: selectedItem?.config?.ctaText,
              }}
              fullWidth={true}
            />
            {selectedItem?.extraText && (
              <div className="pt-6 flex flex-row text-left mb-3 justify-start pb-4">
                <ThumbUp className="h-[25px] w-[25px] shrink-0" />
                <Typography
                  className="text-xs text-[#5C708A] whitespace-nowrap"
                  content={selectedItem.extraText}
                />
              </div>
            )}
          </div>
        )}

        <TrustPilotBadge trustPilot={trustPilot} className="mt-7 hidden lg:flex lg:justify-start" />
      </div>
    </div>
  )
}

// ─── DefaultContent ─────────────────────────────────────────────────────────────

function DefaultContent({
  variant,
  eyebrow,
  finalTitle,
  finalSubtitle,
  button,
  buttonLink,
  graduateButton,
  graduate,
  trustPilot,
  trustpilotInfo,
  ReviewsSectionArray,
  viewRef,
}) {
  const { state: { leadId } = {} } = useContext(LayoutContext)
  const { totalRating: rating, totalReviews } = ReviewsSectionArray?.jsonContent || {}
  const stringToNumber = (str) => {
    if (str) return Number(str.replace(/,/g, ''))
    return null
  }
  const ratingToNumber = stringToNumber(rating)
  const totalReviewsToNumber = stringToNumber(totalReviews)
  const { outOf, link, color } = trustpilotInfo?.jsonContent || {}

  return (
    <div
      className={
        variant === XL_VARIANT ? 'max-w-[580px] xl:min-w-[580px]' : 'max-w-[520px] xl:min-w-[520px]'
      }
      ref={viewRef}
      data-testid="hero-content"
    >
      {eyebrow?.textContent && (
        <Typography
          content={eyebrow.textContent}
          variant="labelSm"
          color="gray"
          fontWeight="bold"
          className="uppercase pb-4 tracking-eyebrow text-center md:text-left"
          data-testid="hero-default-content-eyebrow"
        />
      )}
      {finalTitle?.textContent && (
        <Typography
          content={finalTitle.textContent}
          variant="displayXl"
          mobileVariant="displayLg"
          responsive={true}
          className="pb-4 md:text-[68px]! text-center md:text-left"
          data-testid="hero-default-content-title"
        />
      )}
      {finalSubtitle?.textContent && (
        <Typography
          content={finalSubtitle.textContent}
          variant="bodyLg"
          mobileVariant="labelMd"
          responsive={true}
          className="pb-4 font-normal! text-center md:text-left"
          fontWeight="regular"
          data-testid="hero-default-content-subtitle"
        />
      )}

      {button && (
        <LinkButton
          track={{
            list_name: 'Hero Slider CTA click',
            click_id: button?.linkText?.json?.content?.[0]?.content[0]?.value,
            click_text: button?.linkText?.json?.content?.[0]?.content[0]?.value,
            click_url: button?.linkHref,
            track_event: 'internal_campaign_click',
            event_action: 'button_click',
            event_type: 'button_click',
            ...button.event,
          }}
          typographicOptions={{
            fontWeight: 'bold',
          }}
          variant="contained"
          color="primary"
          className="shadow-md w-full lg:w-auto mt-2"
          content={button}
          data-testid="hero-default-content-button"
        />
      )}

      <div
        className={classNames('flex flex-col items-center lg:justify-start', {
          'w-full lg:w-fit justify-center': ratingToNumber && totalReviews && outOf && link,
        })}
      >
        {buttonLink && (
          <AchieveLink
            href={buttonLink?.linkHref?.replace('{leadId}', leadId)}
            track={{
              force_custom_event_name: true,
              page_name: 'FDR-Web | Freedom Debt Relief Reviews',
              list_name: `FDR-Web | ${buttonLink?.text}`,
              click_id: buttonLink?.linkText?.json?.content?.[0]?.content[0]?.value,
              click_text: `FDR-Web | ${buttonLink?.text}`,
              click_url: buttonLink?.linkHref?.replace('{leadId}', leadId),
              track_event: 'button_click',
              event_action: 'button_click',
              event_type: 'button_click',
              nav_link_section: 'Hero Card',
              page_section: 'Our clients say it best',
              event_name: 'FDR-Web | Freedom Debt Relief Reviews - button_click',
            }}
          >
            <Button
              className={classNames(
                'mt-0 lg:mt-4 bg-[#CB000E] hover:bg-[#1A2D54] px-6 py-3 rounded-md',
                `${ratingToNumber && totalReviews && outOf && link ? 'w-[278px]' : 'w-full'}`
              )}
              variant="contained"
              data-testid="hero-brand-primary-button"
              id="hero-brand-primary-button"
            >
              {buttonLink?.linkText?.json.content[0]?.content[0]?.value}
            </Button>
          </AchieveLink>
        )}
        {ratingToNumber && totalReviews && outOf && link && (
          <div className="mt-3 lg:mt-4 lg:justify-self-start">
            <TrustpilotRatingBadge
              rating={ratingToNumber}
              totalReviews={totalReviewsToNumber}
              outOf={outOf}
              link={link}
              color={color}
            />
          </div>
        )}
      </div>

      {graduateButton && graduate && (
        <LinkButton
          track={{
            list_name: 'Hero Slider Graduate CTA click',
            click_id: graduateButton?.linkText?.json?.content?.[0]?.content[0]?.value,
            click_text: graduateButton?.linkText?.json?.content?.[0]?.content[0]?.value,
            click_url: graduateButton?.linkHref,
            track_event: 'internal_campaign_click',
            event_action: 'button_click',
            event_type: 'button_click',
            ...graduateButton.event,
          }}
          typographicOptions={{
            fontWeight: 'bold',
          }}
          variant="contained"
          color="primary"
          className="w-full lg:w-fit mt-0 lg:mt-4 bg-[#CB000E] hover:bg-[#1A2D54] hover:text-white px-6 py-3 rounded-md"
          content={graduateButton}
        />
      )}

      <TrustPilotBadge trustPilot={trustPilot} className="mt-7 lg:justify-start" />
    </div>
  )
}

// ─── Main Module ────────────────────────────────────────────────────────────────

const HeroSliderModule = memo(function HeroSliderModule({
  grid,
  trustPilot,
  title,
  subTitle,
  graduateTitle,
  graduateSubTitle,
  eyebrow,
  config,
  button,
  graduateButton,
  buttonLink,
  trustMarkersGrid,
  trustpilotInfo,
  ReviewsSectionArray,
}) {
  const { graduate } = useGraduate()

  const items = useMemo(
    () => grid?.gridItemsCollection?.items || [],
    [grid?.gridItemsCollection?.items]
  )

  const backgroundConfig = config?.jsonContent?.sectionBackground ?? 'bgDefault'
  const finalTitle = graduate && graduateTitle ? graduateTitle : title
  const finalSubtitle = graduate && graduateSubTitle ? graduateSubTitle : subTitle
  const variant = config?.jsonContent?.variant ?? 'default'
  const tabStyle = config?.jsonContent?.tabStyle ?? 'default'
  const isTabsVariant = variant === TABS_VARIANT
  const isLending = tabStyle === LENDING_VARIANT
  const isQuote = tabStyle === TAB_QUOTE_STYLE

  const trustMarkers = useMemo(
    () => trustMarkersGrid?.gridItemsCollection?.items[0]?.gridItemsCollection?.items || [],
    [trustMarkersGrid]
  )
  const hasTrustMarkers = trustMarkers.length > 0

  const { fireAndForget } = useTriggerEvents()
  const [selected, setSelected] = useState(0)
  const [selectedItem, setSelectedItem] = useState(items[0])
  const isUserActive = useInactivityStatus()
  const { ref: viewRef, isInView } = useIsInViewport()
  const viewCountRef = useRef([])
  const carouselRef = useRef(null)

  // Track slide views — use a ref for the count array to avoid stale closures
  useEffect(() => {
    const counts = viewCountRef.current
    counts[selected] = (counts[selected] || 0) + 1
    fireAndForget({
      event_type: 'track',
      slide_viewed: items[selected]?.eyebrow,
      slide_viewed_count: counts[selected],
      track_event: 'track',
      event_action: 'track',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem])

  const handleTabClick = useCallback((index) => {
    carouselRef.current?.goToSlide(index)
  }, [])

  const handleTabsSlideChange = useCallback(
    (index) => {
      const adjustedIndex = index - 1
      setSelected(adjustedIndex)
      setSelectedItem(items[adjustedIndex])
    },
    [items]
  )

  const handleDefaultSlideChange = useCallback(
    (index) => {
      setSelected(index)
      setSelectedItem(items[index])
    },
    [items]
  )

  return (
    <Section
      variant={backgroundConfig}
      data-testid={isTabsVariant ? 'tab-slider-container' : 'hero-slider-container'}
    >
      <div
        className={classNames(
          'flex flex-col lg:flex-row justify-between',
          isLending ? 'p-0 lg:p-0 lg:items-end' : 'p-0 md:p-4 lg:pt-4 lg:px-0',
          isTabsVariant ? 'pt-4' : 'pt-12',
          isQuote ? 'items-start' : 'items-center',
          { 'mt-0 lg:mt-4': hasTrustMarkers }
        )}
        data-testid="hero-wrapper"
      >
        {/* Left Panel */}
        {isTabsVariant ? (
          <TabsContent
            items={items}
            selected={selected}
            selectedItem={selectedItem}
            tabStyle={tabStyle}
            trustPilot={trustPilot}
            carouselRef={carouselRef}
            viewRef={viewRef}
            onTabClick={handleTabClick}
          />
        ) : (
          <DefaultContent
            variant={variant}
            eyebrow={eyebrow}
            finalTitle={finalTitle}
            finalSubtitle={finalSubtitle}
            button={button}
            buttonLink={buttonLink}
            graduateButton={graduateButton}
            graduate={graduate}
            trustPilot={trustPilot}
            trustpilotInfo={trustpilotInfo}
            ReviewsSectionArray={ReviewsSectionArray}
            viewRef={viewRef}
          />
        )}

        {/* Right Panel — Carousel */}
        {hasTrustMarkers ? (
          <TrustMarkersCarousel trustMarkers={trustMarkers} items={items} />
        ) : isTabsVariant ? (
          <TabsCarousel
            items={items}
            tabStyle={tabStyle}
            variant={variant}
            isUserActive={isUserActive}
            isInView={isInView}
            carouselRef={carouselRef}
            onSlideChange={handleTabsSlideChange}
            trustPilot={trustPilot}
          />
        ) : (
          <DefaultCarousel
            items={items}
            tabStyle={tabStyle}
            selected={selected}
            onSlideChange={handleDefaultSlideChange}
            trustPilot={trustPilot}
          />
        )}
      </div>
    </Section>
  )
})

// ─── Entry Point ────────────────────────────────────────────────────────────────

const CONTENT_KEYS = [
  'grid',
  'trustPilot',
  'title',
  'subTitle',
  'graduateTitle',
  'graduateSubTitle',
  'eyebrow',
  'config',
  'button',
  'graduateButton',
  'buttonLink',
  'trustMarkersGrid',
  'trustpilotInfo',
  'ReviewsSectionArray',
]

function HeroSlider({ content }) {
  const {
    grid,
    trustPilot,
    title,
    subTitle,
    graduateTitle,
    graduateSubTitle,
    eyebrow,
    config,
    button,
    graduateButton,
    buttonLink,
    trustMarkersGrid,
    trustpilotInfo,
    ReviewsSectionArray,
  } = useMemoizedContentGetter(content, CONTENT_KEYS)
  return (
    <HeroSliderModule
      title={title}
      subTitle={subTitle}
      eyebrow={eyebrow}
      grid={grid}
      trustPilot={trustPilot}
      config={config}
      button={button}
      buttonLink={buttonLink}
      graduateTitle={graduateTitle}
      graduateSubTitle={graduateSubTitle}
      graduateButton={graduateButton}
      trustMarkersGrid={trustMarkersGrid}
      trustpilotInfo={trustpilotInfo}
      ReviewsSectionArray={ReviewsSectionArray}
    />
  )
}

export { HeroSlider, HeroSliderModule }
export default HeroSlider
